"use client";

import useLocaleLegacy from "@/hooks/use-locale-legacy";
import Image from "next/image";
import locale from "@/locales/footer.json";
import { useSearchParams } from "next/navigation";
import { Link } from "@/src/i18n/navigation";
import extractPhoneNumber from "@/lib/extract-phone-number";

export interface FooterProps {
  phone_number: string;
  wa_phone_number: string;
  email: string;
  detail: string;
  phone_icon: string;
  wa_phone_icon: string;
  email_icon: string;
  social_media: { id: string; link: string; image: string }[];
  menu: { id: string; text: string; link: string }[];
  address: string;
  address_gmaps: string;
}

export default function Footer(props: FooterProps) {
  const { t } = useLocaleLegacy(locale);
  const qs = useSearchParams();

  return (
    <>
      {/* mobile version */}
      <footer className="relative block xl:hidden bg-coffebrown">
        <div className="grid grid-cols-[1fr_164px] items-end -translate-y-1/2">
          <div className="footer-top-left overflow-hidden h-[49px] w-[101%] text-white bg-white">
            <div className="w-[100vw] h-full"></div>
          </div>
          <div className="footer-top-right">
            <img src="/CIM-short.png" alt="footer logo" className="h-[61px]" />
          </div>
        </div>

        <div className="px-10 py-3">
          <p className="text-white font-pluto">Marketing Office</p>

          <div className="text-white mt-4">
            <p className="font-bold">The Hub Sinar Mas Land</p>
            <Link target="_blank" href={props.address_gmaps || ""}>
              <p
                dangerouslySetInnerHTML={{ __html: props.address }}
              ></p>
            </Link>

            <p>
              Opening Hours: <br />
              10:00 - 18:00 WIB
            </p>
          </div>

          <div className="w-full mt-4 space-y-3">
            <div className="flex items-center">
              <Link
                className="inline-flex items-center"
                href={`tel:+${props.phone_number}`}
                target="_blank"
              >
                <Image
                  loading="lazy"
                  src={props.phone_icon}
                  alt=""
                  width={20}
                  height={20}
                />
                <p className="ml-1 text-white ">{props.phone_number}</p>
              </Link>
            </div>

            <div className="flex items-center">
              <Link
                className="inline-flex items-center"
                target="_blank"
                href={`https://wa.me/${extractPhoneNumber(
                  props.wa_phone_number
                )}`}
              >
                <Image
                  loading="lazy"
                  src={props.wa_phone_icon}
                  alt=""
                  width={20}
                  height={20}
                />
                <p className="ml-1 text-white ">{props.wa_phone_number}</p>
              </Link>
            </div>

            <div className="flex items-center">
              <Link
                className="inline-flex items-center"
                href={`mailto:${props.email}`}
                target="_blank"
              >
                <Image
                  loading="lazy"
                  src={props.email_icon}
                  alt=""
                  width={20}
                  height={20}
                />
                <p className="ml-1 text-white ">{props.email}</p>
              </Link>
            </div>
          </div>
        </div>

        <div className="columns-2 h-full text-white  mt-6 px-10">
          {props.menu.map((menu) => {
            const url = new URL(menu.link, process.env.NEXT_PUBLIC_APP_URL);
            url.search = new URLSearchParams(qs).toString();

            return (
              <Link prefetch key={menu.id} href={url.toString()}>
                <div className="mb-1">
                  <span className="cursor-pointer ">{menu.text}</span>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-6">
          <p className="text-white text-center ">{t("follow_us")}</p>
          <div className="inline-flex items-center space-x-1 justify-center w-full">
            {props.social_media.map((social) => {
              return (
                <Link key={social.id} target="_blank" href={social.link}>
                  <img
                    loading="lazy"
                    src={social.image}
                    alt=""
                    width={20}
                    height={20}
                    className="w-8 h-8 object-cover"
                  />
                </Link>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col items-center mt-6 px-10 pb-3">
          <p className="flex-1 text-white ">
            © 2025 | The Elements. All Right Reserved
          </p>

          <div className="flex-1 text-white col-span-9">
            <Link className="" href={"/privacy-policy"}>
              Privacy Policy
            </Link>
            <span className="mx-0.5 ">|</span>
            <Link className="" href={"/terms-conditions"}>
              Terms and Conditions
            </Link>
          </div>
        </div>
      </footer>

      <footer className="relative hidden xl:block bg-coffebrown">
        <div className="grid grid-cols-[1fr_220px] xl:grid-cols-[1fr_231px] items-end -translate-y-1/2">
          <div className="footer-top-left overflow-hidden h-[49px] xl:h-[69px] w-[101%] text-white bg-white">
            <div className="w-[100vw] h-full"></div>
          </div>
          <div className="footer-top-right">
            <img
              src="/CIM-short.png"
              alt="footer logo"
              className="h-[64px] xl:h-[86px]"
            />
          </div>
        </div>

        <div className="pb-10">
          <div className="grid grid-cols-12 w-full xl:max-w-screen-xl mx-auto">
            <div className="col-span-3 flex flex-col items-center">
              <img
                src="/elements-logo.png"
                alt=""
                className="w-44 h-40 object-cover"
              />

              <div className="mt-auto">
                <p className="text-white text-center text-2xl">
                  {t("follow_us")}
                </p>
                <div className="inline-flex items-center space-x-1 justify-center w-full">
                  {props.social_media.map((social) => {
                    return (
                      <Link key={social.id} target="_blank" href={social.link}>
                        <img
                          loading="lazy"
                          width={32}
                          height={32}
                          src={social.image}
                          alt=""
                        />
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="col-span-4 flex flex-col justify-around pl-16">
              <p className="text-3xl text-white font-pluto">
                Marketing Gallery
              </p>

              <div className="text-white text-xl mt-4">
                <p className="font-bold">The Hub Sinar Mas Land</p>
                <Link target="_blank" href={props.address_gmaps || ""}>
                  <p
                    className="mb-6"
                    dangerouslySetInnerHTML={{ __html: props.address }}
                  ></p>
                </Link>

                <p>
                  Opening Hours: <br />
                  10:00 - 18:00 WIB
                </p>
              </div>

              <div className="w-full mt-3 space-y-3">
                <div className="flex items-center ">
                  <Link
                    className="inline-flex items-center"
                    href={`tel:+${props.phone_number}`}
                    target="_blank"
                  >
                    <Image
                      loading="lazy"
                      src={props.phone_icon}
                      alt=""
                      width={32}
                      height={32}
                    />
                    <p className="ml-3 text-white text-xl">
                      {props.phone_number}
                    </p>
                  </Link>
                </div>

                <div className="flex items-center">
                  <Link
                    className="inline-flex items-center"
                    target="_blank"
                    href={`https://wa.me/${extractPhoneNumber(
                      props.wa_phone_number
                    )}`}
                  >
                    <Image
                      loading="lazy"
                      src={props.wa_phone_icon}
                      alt=""
                      width={32}
                      height={32}
                    />
                    <p className="ml-3 text-white text-xl">
                      {props.wa_phone_number}
                    </p>
                  </Link>
                </div>

                <div className="flex items-center">
                  <Link
                    className="inline-flex items-center"
                    href={`mailto:${props.email}`}
                    target="_blank"
                  >
                    <Image
                      loading="lazy"
                      src={props.email_icon}
                      alt=""
                      width={32}
                      height={32}
                    />
                    <p className="ml-3 text-white text-xl">{props.email}</p>
                  </Link>
                </div>
              </div>
            </div>

            <div className="col-span-5 ml-24 flex flex-col">
              <div
                className="columns-2 h-full text-white text-xl gap-y-1.5"
                style={{ columnGap: "3rem" }}
              >
                {props.menu.map((menu) => {
                  const url = new URL(
                    menu.link,
                    process.env.NEXT_PUBLIC_APP_URL
                  );
                  url.search = new URLSearchParams(qs).toString();
                  return (
                    <Link prefetch key={menu.id} href={url.toString()}>
                      <div className="mb-1">
                        <span className="cursor-pointer text-2xl">
                          {menu.text}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>

              <div className="w-full xl:max-w-screen-xl mx-auto mt-16 grid grid-cols-12">
                <div className="col-span-10">
                  <div className="text-white text-right">
                    <Link
                      target="_blank"
                      className="text-sm "
                      href={"/privacy-policy"}
                    >
                      Privacy Policy
                    </Link>
                    <span className="mx-0.5">|</span>
                    <Link
                      target="_blank"
                      className="text-sm "
                      href={"/terms-conditions"}
                    >
                      Terms and Conditions
                    </Link>
                  </div>

                  <p className="mt-auto text-right text-white">
                    © 2025 | The Elements. All Rights Reserved.
                  </p>
                </div>

                <div className="col-span-2"></div>
              </div>
            </div>
          </div>

          <div className="w-full xl:max-w-screen-xl mx-auto mt-11 text-center">
            <p className="text-white text-[11px]">
              DISCLAIMER. All content on this website is for illustration and
              reference only. The developer is not liable for any changes,
              errors, or omissions. Visuals are artist impressions and may
              differ from the final product. Only the Sale and Purchase
              Agreement is binding.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}
